import { Component, OnInit } from '@angular/core';
import { BitrixWebhookService } from '../../services/bitrix-webhook.service';
import {
  BitrixWebhookRequest,
  BitrixWebhookRequestFilter,
  BitrixWebhookStats,
  BitrixRequestStatus,
  BitrixRequestType
} from '../../interfaces/bitrix-webhook.interfaces';
import { ToastService } from '../../services/toast.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-bitrix-requests',
  templateUrl: './bitrix-requests.component.html',
  styleUrls: ['./bitrix-requests.component.scss']
})
export class BitrixRequestsComponent implements OnInit {
  requests: BitrixWebhookRequest[] = [];
  filteredRequests: BitrixWebhookRequest[] = [];
  stats: BitrixWebhookStats | null = null;
  loading = false;
  
  // Filtros
  filter: BitrixWebhookRequestFilter = {};
  statusFilter: BitrixRequestStatus | null = null;
  searchText = '';
  
  // Enums para el template
  BitrixRequestStatus = BitrixRequestStatus;
  BitrixRequestType = BitrixRequestType;

  constructor(
    private bitrixService: BitrixWebhookService,
    private toastService: ToastService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    
    // Cargar solicitudes
    this.bitrixService.getRequests(this.filter).subscribe({
      next: (data) => {
        this.requests = data;
        this.applyFilters();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error cargando solicitudes:', error);
        this.toastService.showError('Error cargando solicitudes de Bitrix');
        this.loading = false;
      }
    });

    // Cargar estadísticas
    this.bitrixService.getStats().subscribe({
      next: (stats) => {
        this.stats = stats;
      },
      error: (error) => {
        console.error('Error cargando estadísticas:', error);
      }
    });
  }

  applyFilters(): void {
    this.filteredRequests = this.requests.filter(request => {
      // Filtro por estado
      if (this.statusFilter !== null && request.status !== this.statusFilter) {
        return false;
      }

      // Filtro por texto (busca en leadId, email, nombre, nemónico)
      if (this.searchText) {
        const searchLower = this.searchText.toLowerCase();
        const matchesSearch = 
          request.leadId.toLowerCase().includes(searchLower) ||
          (request.participantEmail && request.participantEmail.toLowerCase().includes(searchLower)) ||
          (request.participantName && request.participantName.toLowerCase().includes(searchLower)) ||
          (request.projectMnemonic && request.projectMnemonic.toLowerCase().includes(searchLower));
        
        if (!matchesSearch) {
          return false;
        }
      }

      return true;
    });
  }

  onStatusFilterChange(): void {
    this.applyFilters();
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  clearFilters(): void {
    this.statusFilter = null;
    this.searchText = '';
    this.filter = {};
    this.applyFilters();
  }

  viewDetail(request: BitrixWebhookRequest): void {
    this.router.navigate(['/bitrix-requests', request.id]);
  }

  retryRequest(request: BitrixWebhookRequest, event: Event): void {
    event.stopPropagation();
    
    if (request.status === BitrixRequestStatus.Success) {
      this.toastService.showWarning('Esta solicitud ya fue procesada exitosamente');
      return;
    }

    if (request.status === BitrixRequestStatus.Processing) {
      this.toastService.showWarning('Esta solicitud está siendo procesada actualmente');
      return;
    }

    if (confirm(`¿Está seguro de reprocesar la solicitud Lead ID: ${request.leadId}?`)) {
      this.bitrixService.retryProcessing(request.id).subscribe({
        next: (response) => {
          this.toastService.showSuccess(response.message);
          this.loadData();
        },
        error: (error) => {
          console.error('Error reprocesando solicitud:', error);
          this.toastService.showError(error.error || 'Error al reprocesar la solicitud');
        }
      });
    }
  }

  getStatusName(status: BitrixRequestStatus): string {
    return this.bitrixService.getStatusName(status);
  }

  getStatusBadgeClass(status: BitrixRequestStatus): string {
    return this.bitrixService.getStatusBadgeClass(status);
  }

  getStatusIcon(status: BitrixRequestStatus): string {
    return this.bitrixService.getStatusIcon(status);
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-ES');
  }

  formatProcessingTime(timeMs: number | null | undefined): string {
    if (!timeMs) return '-';
    
    if (timeMs < 1000) {
      return `${timeMs}ms`;
    } else if (timeMs < 60000) {
      return `${(timeMs / 1000).toFixed(1)}s`;
    } else {
      const minutes = Math.floor(timeMs / 60000);
      const seconds = Math.floor((timeMs % 60000) / 1000);
      return `${minutes}m ${seconds}s`;
    }
  }

  getCommonErrorsArray(): { step: string, count: number }[] {
    if (!this.stats || !this.stats.commonErrors) return [];
    return Object.entries(this.stats.commonErrors)
      .map(([step, count]) => ({ step, count }))
      .sort((a, b) => b.count - a.count);
  }

  getTopProjectsArray(): { project: string, count: number }[] {
    if (!this.stats || !this.stats.topProjects) return [];
    return Object.entries(this.stats.topProjects)
      .map(([project, count]) => ({ project, count }))
      .sort((a, b) => b.count - a.count);
  }

  getRequestTypeName(type: BitrixRequestType): string {
    switch (type) {
      case BitrixRequestType.Inbound: return 'Entrada';
      case BitrixRequestType.Outbound: return 'Salida';
      default: return 'Entrada';
    }
  }

  getRequestTypeBadgeClass(type: BitrixRequestType): string {
    switch (type) {
      case BitrixRequestType.Inbound: return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case BitrixRequestType.Outbound: return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  }

  getRequestTypeIcon(type: BitrixRequestType): string {
    switch (type) {
      case BitrixRequestType.Inbound: return 'M19 14l-7 7m0 0l-7-7m7 7V3'; // Arrow down
      case BitrixRequestType.Outbound: return 'M5 10l7-7m0 0l7 7m-7-7v18'; // Arrow up
      default: return 'M19 14l-7 7m0 0l-7-7m7 7V3';
    }
  }
}

